import React, { useEffect, useState } from 'react';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';

interface LocalPoints {
  balance: number;
  redeemable_rewards: number;
  points_to_next_reward: number;
  lifetime_earned: number;
  lifetime_redeemed: number;
}

/**
 * Points loyalty card for local (Kettle Falls) Bridge Club members.
 *
 * Reads the SAME LocalPointsBalance source the POS reads (via the existing
 * /local-points/ endpoint) so the member app and the till never disagree.
 * $1 spent = 1 point; 500 points = a $10 reward.
 *
 * Render this ONLY for local_access members — the parent gates on that.
 */
const LocalPointsCard: React.FC = () => {
  const [points, setPoints] = useState<LocalPoints | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await bridgeClubAPI.getLocalPoints();
        if (active) setPoints(data.local_points);
      } catch {
        if (active) setPoints(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="stats-card">
        <h3>Rewards Points</h3>
        <p style={{ color: '#666' }}>Loading your points…</p>
      </div>
    );
  }
  if (!points) return null;

  const progressPct = Math.min(100, ((500 - points.points_to_next_reward) / 500) * 100);

  return (
    <div className="stats-card">
      <h3>Rewards Points</h3>
      <div className="stats-grid">
        <div className="stat-item">
          <span className="stat-value">{points.balance}</span>
          <span className="stat-label">Points Balance</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{500 - points.points_to_next_reward}/500</span>
          <span className="stat-label">Toward Next $10</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{points.redeemable_rewards}</span>
          <span className="stat-label">Rewards Ready</span>
        </div>
      </div>

      {/* Progress toward the next $10 reward */}
      <div style={{ marginTop: 12 }}>
        <div style={{
          height: 10, borderRadius: 5, background: '#E5E7EB', overflow: 'hidden',
        }}>
          <div style={{
            width: `${progressPct}%`, height: '100%',
            background: '#2D5A27', transition: 'width 0.3s',
          }} />
        </div>
        <p style={{ fontSize: 13, color: '#444', marginTop: 6 }}>
          {points.redeemable_rewards > 0 ? (
            <strong style={{ color: '#2D5A27' }}>
              🎉 {points.redeemable_rewards} reward{points.redeemable_rewards === 1 ? '' : 's'} available
              (${points.redeemable_rewards * 10}) — redeem at the mill.
            </strong>
          ) : (
            <>{points.points_to_next_reward} more point{points.points_to_next_reward === 1 ? '' : 's'} to your next $10 reward.</>
          )}
        </p>
        <p style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          $1 spent = 1 point · 500 points = $10 off. Lifetime earned {points.lifetime_earned}.
        </p>
      </div>
    </div>
  );
};

export default LocalPointsCard;
